import validator from "validator";

/**
 * TypeScript port of famautils.account_urls (fama-interface-package).
 * Keep normalize/validate behavior aligned with that module.
 * User-facing PROFILE_URL_ERROR copy may differ from backend MSG_* strings.
 *
 * Cleanup: trim → reject internal whitespace/backslash → default https →
 * drop fragment → drop query except Facebook profile.php?id= → validate URL
 * → reject if normalized length > 255. Do not silently truncate.
 *
 * The old "check raw varchar(255) because that is what the DB stores" idea
 * was already wrong: person and item never stored the submitted string.
 * FEI and parse_profile each cleaned first, but differently, so length and
 * whitespace failed after ACK. See famautils.account_urls for the full note.
 */

/** Matches person-db / MySQL varchar(255) columns used by check/2025. */
export const DB_VARCHAR_255 = 255;

export const PROFILE_URL_ERROR = {
  empty: "Please enter a valid profile URL",
  illegal: "Profile URL contains invalid characters or whitespace",
  invalid: "Please enter a valid profile URL",
  tooLong: "Profile URL must not exceed 255 characters",
  duplicate: "Profile duplicated, please ensure they are unique",
} as const;

const HAS_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;
const ILLEGAL_CHARS = /[\s\\]/;

export type NameParts = {
  prefix?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
};

export type ProfileUrlValidation =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

/** Mirrors external-interface Name.full_name join order. */
export function buildCombinedFullName(parts: NameParts): string {
  return [
    parts.prefix,
    parts.firstName,
    parts.middleName,
    parts.lastName,
    parts.suffix,
  ]
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    .join(" ");
}

export function isCombinedFullNameWithinDbLimit(parts: NameParts): boolean {
  return buildCombinedFullName(parts).length <= DB_VARCHAR_255;
}

/**
 * Trim, default https, drop fragment and non-Facebook-id query params.
 * Does not truncate. Returns "" for empty/whitespace-only input.
 * Mirrors famautils.account_urls.normalize_account_url.
 *
 * Does not pass strings with internal whitespace or backslash to `new URL()`:
 * that constructor percent-encodes spaces and turns `\` into `/`, which would
 * silently "fix" input that validate must reject. Python urlparse keeps those
 * characters; this matches that.
 */
export function normalizeProfileUrl(url: string): string {
  let cleaned = (url ?? "").trim();
  if (!cleaned) {
    return "";
  }
  if (ILLEGAL_CHARS.test(cleaned)) {
    return cleaned;
  }
  if (!HAS_SCHEME.test(cleaned)) {
    cleaned = `https://${cleaned}`;
  }
  let parsed: URL;
  try {
    parsed = new URL(cleaned);
  } catch {
    return cleaned;
  }
  const path = parsed.pathname || "";
  let query = "";
  if (path.includes("profile.php")) {
    const id = parsed.searchParams.get("id");
    if (id) {
      query = `id=${encodeURIComponent(id)}`;
    }
  }
  const normalized = `${parsed.protocol}//${parsed.host}${path}${
    query ? `?${query}` : ""
  }`;
  return normalized;
}

export function getProfileUrlValidation(
  url: string,
  options: { allowEmpty?: boolean } = {}
): ProfileUrlValidation {
  const trimmed = (url ?? "").trim();
  if (!trimmed) {
    return options.allowEmpty
      ? { ok: true, normalized: "" }
      : { ok: false, message: PROFILE_URL_ERROR.empty };
  }
  if (ILLEGAL_CHARS.test(trimmed)) {
    return { ok: false, message: PROFILE_URL_ERROR.illegal };
  }
  const normalized = normalizeProfileUrl(trimmed);
  if (normalized.length > DB_VARCHAR_255) {
    return { ok: false, message: PROFILE_URL_ERROR.tooLong };
  }
  if (
    !validator.isURL(normalized, {
      allow_underscores: true,
    })
  ) {
    return { ok: false, message: PROFILE_URL_ERROR.invalid };
  }
  return { ok: true, normalized };
}

/**
 * Value to write back on blur, flush on Next, or use as a duplicate key.
 * Applies cleanup only when the URL passes validation so illegal characters
 * are not rewritten into a valid-looking URL. Invalid input is trimmed only.
 */
export function cleanedProfileUrl(url: string): string {
  const result = getProfileUrlValidation(url, { allowEmpty: true });
  return result.ok ? result.normalized : (url ?? "").trim();
}

/**
 * Profile / account URL rules: normalize (drop query except Facebook id) then
 * validate length against varchar(255). Empty is allowed only when `allowEmpty`.
 */
export function isValidProfileUrl(
  url: string,
  options: { allowEmpty?: boolean } = {}
): boolean {
  return getProfileUrlValidation(url, options).ok;
}

export function profileUrlErrorMessage(
  url: string,
  options: { allowEmpty?: boolean } = {}
): string | undefined {
  const result = getProfileUrlValidation(url, options);
  return result.ok ? undefined : result.message;
}

/** True when both strings clean to the same non-empty profile URL. */
export function profileUrlsMatch(a: string, b: string): boolean {
  const left = cleanedProfileUrl(a);
  return left.length > 0 && left === cleanedProfileUrl(b);
}

export type CollectedProfileUrls = {
  urls: string[];
  duplicateKeys: Set<string>;
  error?: string;
};

/** Clean, unique, and find normalized duplicates in one pass. Blanks are dropped. */
export function collectProfileUrls(
  urls: readonly string[]
): CollectedProfileUrls {
  const unique: string[] = [];
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  let error: string | undefined;

  for (const url of urls) {
    const result = getProfileUrlValidation(url, { allowEmpty: true });
    const cleaned = result.ok ? result.normalized : (url ?? "").trim();
    if (!result.ok && !error && cleaned) {
      error = result.message;
    }
    if (!cleaned) {
      continue;
    }
    counts.set(cleaned, (counts.get(cleaned) ?? 0) + 1);
    if (!seen.has(cleaned)) {
      seen.add(cleaned);
      unique.push(cleaned);
    }
  }

  const duplicateKeys = new Set<string>();
  counts.forEach((count, key) => {
    if (count > 1) {
      duplicateKeys.add(key);
    }
  });
  return error
    ? { urls: unique, duplicateKeys, error }
    : { urls: unique, duplicateKeys };
}

export function collectNormalizedProfileUrls(
  urls: string[]
): { ok: true; urls: string[] } | { ok: false; message: string } {
  const collected = collectProfileUrls(urls);
  if (collected.error) {
    return { ok: false, message: collected.error };
  }
  return { ok: true, urls: collected.urls };
}
